import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Upload,
  message,
} from "antd";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import axios from "axios";
import { config } from "../../config/config";
import CryptoJS from "crypto-js";
import { motion, AnimatePresence } from "framer-motion";
import base64 from "base64-encode-file";
import NINModificationHistory from "./History/NINModificationHistory.jsx";

const MODIFICATION_TYPES = [
  { value: "name", label: "Change of Name", price: 6000 },
  { value: "dob", label: "Date of Birth", price: 40000 },
  { value: "address", label: "Address", price: 6000 },
];

const MAX_FILE_SIZE = 50 * 1024; // 50KB in bytes
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const customUploadStyle = `
  .full-width-uploader .ant-upload-select {
    width: 100% !important;
    height: 180px !important;
    overflow: hidden;
    border-radius: 12px !important;
  }
  .full-width-uploader .ant-upload {
    padding: 0 !important;
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
`;

function Modification() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedModification, setSelectedModification] = useState(null);
  const [passportBase64, setPassportBase64] = useState(null);
  const [passportPreview, setPassportPreview] = useState(null);
  const [modificationTypes, setModificationTypes] = useState([
    { value: "name", label: "Change of Name", price: 6000 },
    { value: "dob", label: "Date of Birth", price: 40000 },
    { value: "address", label: "Address", price: 6000 },
  ]);
  const [priceLoading, setPriceLoading] = useState(true);
  const [refreshHistory, setRefreshHistory] = useState(0);

  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  function decryptData(ciphertext) {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }
  // Get userId from encrypted localStorage
  let userId = null;
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = decryptData(userStr);
      userId = userObj?._id || userObj?.id;
    }
  } catch (error) {}
  const onFinish = async (values) => {
    const selectedType = modificationTypes.find(
      (type) => type.value === values.modificationType
    );
    if (!selectedType) {
      toast.error("Invalid modification type selected.");
      return;
    }
    if (!passportBase64) {
      toast.error("Please upload a passport photograph.");
      return;
    }

    // Show confirmation dialog first
    const result = await Swal.fire({
      title: "Confirm NIN Modification",
      html: `
        <p class="mb-4 text-gray-500">Are you sure you want to submit this NIN modification request?</p>
        <div class="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-2 text-left mb-4 font-mono text-sm">
          <p class="text-sm text-gray-600"><strong>Type:</strong> ${selectedType.label}</p>
          <p class="text-sm text-gray-600"><strong>NIN:</strong> ${values.ninNumber}</p>
          <p class="text-sm text-gray-600"><strong>Name:</strong> ${values.newFirstName || ""} ${values.newSurname || ""}</p>
        </div>
        <div class="bg-sky-50 border-2 border-sky-200 rounded-2xl py-3 px-6 inline-block shadow-sm">
          <span class="text-xs font-bold text-sky-500 uppercase tracking-wider block mb-0.5">Modification Fee</span>
          <span class="text-3xl font-black text-sky-600">₦${selectedType.price.toLocaleString()}</span>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, submit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const basePayload = {
        userId: userId,
        modificationType: selectedType.label,
        modificationAmount: selectedType.price,
        gender: values.gender,
        surName: values.newSurname,
        firstName: values.newFirstName,
        middleName: values.newMiddleName || "",
        phone: values.phone || "",
        ninNumber: values.ninNumber,
        passport: passportBase64,
      };

      const modificationSpecificPayload = {
        dob: { newDob: values.newDob?.format("YYYY-MM-DD") },
        name: {}, // name fields are already in base payload
        phone: { newPhoneNo: values.newPhoneNo },
        address: { newAddress: values.newAddress },
      };

      const payload = {
        ...basePayload,
        ...modificationSpecificPayload[values.modificationType],
      };

      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.Modification}`,
        payload
      );

      await Swal.fire({
        icon: "success",
        title: "Modification Request Submitted!",
        text: "Your modification request has been submitted successfully.",
        confirmButtonColor: "#f59e0b",
      });
      form.resetFields();
      setPassportBase64(null);
      setPassportPreview(null);
      setSelectedModification(null);
      setRefreshHistory((prev) => prev + 1);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          error.response?.data?.error ||
          error.message ||
          "Failed to submit request. Please try again.",
        confirmButtonColor: "#d10",
      });
    } finally {
      setLoading(false);
    }
  };



  // Fetch NIN modification prices
  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true);
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );

        if (Array.isArray(response.data)) {
          const updatedTypes = modificationTypes.map((type) => {
            const apiItem = response.data.find(
              (item) => item.key === `nin_modification_${type.value}`
            );
            return {
              ...type,
              price: apiItem && apiItem.prices ? apiItem.prices.agent : type.price,
            };
          });
          setModificationTypes(updatedTypes);
        }
      } catch (error) {
        
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrices();
  }, []);

  const handleModificationTypeChange = (value) => {
    setSelectedModification(value);
    const selected = modificationTypes.find((type) => type.value === value);
    form.setFieldsValue({ modificationAmount: selected ? selected.price : 0 });
  };

  const handleFileChange = async (e) => {
    const file = e.file.originFileObj || e.file;
    if (file) {
      try {
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          message.error("Only JPG, JPEG and PNG files are allowed");
          return;
        }

        if (file.size > MAX_FILE_SIZE) {
          message.error("File size must be less than 50KB");
          return;
        }

        const dataUrl = await base64(file);
        setPassportPreview(dataUrl);
        setPassportBase64(dataUrl);

        // Manually trigger validation for the Form.Item
        form.setFieldsValue({ passport: dataUrl });
        form.validateFields(["passport"]);
      } catch (error) {
        message.error("Failed to upload passport");
      }
    }
  };

  const handleRemoveFile = () => {
    setPassportBase64(null);
    setPassportPreview(null);
    form.setFieldsValue({ passport: null });
  };

  return (
    <div className="w-full">
      <div className="rounded-2xl mb-10 bg-white p-6 shadow-lg border border-gray-100">
        <style>{customUploadStyle}</style>
        <ToastContainer />
        <div className="flex items-center justify-between mb-6 border-b pb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">NIN Modification</h2>
            <p className="text-sm text-gray-500 mt-1">Modify your NIN details</p>
          </div>
        </div>
        <div className="max-w-2xl mx-auto mt-5">
          {selectedModification && (
            <div className="mb-6 p-4 rounded-2xl bg-sky-50 border-2 border-sky-200 text-sky-600 text-center shadow-sm max-w-sm mx-auto animate-fadeIn">
              <span className="text-xs font-bold uppercase tracking-wider block mb-0.5">Modification Fee</span>
              <span className="font-black text-2xl">
                ₦{priceLoading ? "..." : (modificationTypes.find((type) => type.value === selectedModification)?.price || 0).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
          )}
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            requiredMark={true}
          >
            <Form.Item
              label="Select Modification Type"
              name="modificationType"
              rules={[
                { required: true, message: "Please select modification type" },
              ]}
            >
              <Select
                size="large"
                placeholder="Select modification type"
                onChange={handleModificationTypeChange}
              >
                {modificationTypes.map((type) => (
                  <Select.Option key={type.value} value={type.value}>
                    {type.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

        <AnimatePresence>
          {selectedModification && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Form.Item
                name="newSurname"
                label={
                  selectedModification === "name" ? "New Surname" : "Surname"
                }
                rules={[{ required: true }]}
              >
                <Input
                  size="large"
                  placeholder={
                    selectedModification === "name"
                      ? "Enter New Surname"
                      : "Enter Surname"
                  }
                />
              </Form.Item>
              <Form.Item
                name="newFirstName"
                label={
                  selectedModification === "name"
                    ? "New First Name"
                    : "First Name"
                }
                rules={[{ required: true }]}
              >
                <Input
                  size="large"
                  placeholder={
                    selectedModification === "name"
                      ? "Enter New First Name"
                      : "Enter First Name"
                  }
                />
              </Form.Item>
              <Form.Item
                name="newMiddleName"
                label={
                  selectedModification === "name"
                    ? "New Middle Name"
                    : "Middle Name"
                }
              >
                <Input
                  size="large"
                  placeholder={
                    selectedModification === "name"
                      ? "Enter New Middle Name"
                      : "Enter Middle Name"
                  }
                />
              </Form.Item>
            </motion.div>
          )}

          {selectedModification === "dob" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Form.Item
                name="newDob"
                label="New Date of Birth"
                rules={[{ required: true }]}
              >
                <DatePicker className="w-full" size="large" />
              </Form.Item>
            </motion.div>
          )}

          {selectedModification === "address" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Form.Item
                name="newAddress"
                label="New Address"
                rules={[{ required: true }]}
              >
                <Input.TextArea size="large" placeholder="Enter new address" />
              </Form.Item>
            </motion.div>
          )}

          {selectedModification === "phone" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Form.Item
                name="newPhoneNo"
                label="New Phone Number"
                rules={[
                  { required: true },
                  {
                    pattern: /^\d{11}$/,
                    message: "Phone number must be 11 digits",
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter new phone number"
                  maxLength={11}
                />
              </Form.Item>
            </motion.div>
          )}
        </AnimatePresence>

        <Form.Item
          name="gender"
          label="Gender"
          rules={[{ required: true, message: "Please select gender" }]}
        >
          <Select size="large" placeholder="-- Select Gender --">
            <Select.Option value="male">Male</Select.Option>
            <Select.Option value="female">Female</Select.Option>
          </Select>
        </Form.Item>

        {selectedModification && selectedModification !== "phone" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Form.Item
              name="phone"
              label="Phone Number"
              rules={[
                { required: true, message: "Please enter your phone number" },
                {
                  pattern: /^\d{11}$/,
                  message: "Phone number must be 11 digits",
                },
              ]}
            >
              <Input
                size="large"
                placeholder="Enter your phone number"
                maxLength={11}
              />
            </Form.Item>
          </motion.div>
        )}
        <Form.Item
          name="ninNumber"
          label="NIN Number"
          rules={[{ required: true, message: "Please enter NIN number" }]}
        >
          <Input size="large" placeholder="NIN" />
        </Form.Item>


        <Form.Item
          name="passport"
          label="Passport Photograph"
          rules={[
            {
              required: true,
              message: "Please upload your passport photograph",
            },
          ]}
        >
          <Upload
            listType="picture-card"
            showUploadList={false}
            customRequest={handleFileChange}
            className="full-width-uploader"
            accept={ALLOWED_FILE_TYPES.join(",")}
          >
            {passportPreview ? (
              <div className="relative group w-[120px] h-[150px] overflow-hidden rounded-lg border border-gray-200 shadow-sm flex items-center justify-center bg-gray-50">
                <img
                  src={passportPreview}
                  alt="Passport"
                  className="w-full h-full object-cover transition-all duration-300 group-hover:blur-[3px]"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                    className="bg-red-500 text-white px-2.5 py-1 rounded-md text-xs font-semibold hover:bg-red-600 shadow-md transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="text-gray-500">Upload Passport</div>
                <div className="mt-1 text-xs text-gray-400">
                  JPG/PNG (max: 50KB)
                </div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item name="modificationAmount" hidden>
          <Input type="number" />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            className="w-full flex items-center justify-center bg-sky-500 hover:bg-sky-600"
            loading={loading}
          >
            Submit Modification {priceLoading || !selectedModification ? "" : `(₦${(modificationTypes.find(t => t.value === selectedModification)?.price || 0).toLocaleString()})`}
          </Button>
        </Form.Item>
      </Form>
        </div>
      </div>

      {/* History Table */}
      <div className="w-full mt-10">
        <NINModificationHistory key={refreshHistory} />
      </div>
    </div>
  );
}

export default Modification;
